/**
 * inviteAttribution v1.0 — an invite link records who invited whom, once.
 *
 * WHAT BROKE (2026-09-15). Invite emails linked /audit?ref=<token>. The /audit
 * page read the token and posted it to start-trial, which wrote the inviter onto
 * the new signup. /audit was retired (410) and the link moved to /geo-check,
 * which kept the token in localStorage and never gave it to anything that
 * resolves it. Every invite after that would have produced an account with no
 * inviter.
 *
 * HOW IT WORKS NOW, and why it does not depend on the landing page.
 *   1. public/first-touch.js runs on every public page and the SPA shell. When
 *      the address carries ?ref=<uuid> it keeps the token (mf_invite_ref, 30
 *      days, latest wins). So any page an invite opens captures it, and it
 *      survives navigation, a Google sign-in round trip and an email
 *      confirmation in the same browser.
 *   2. Email and password signup also sends the token to register-account,
 *      which stores it on the account, so a confirmation opened in another
 *      browser still carries it.
 *   3. Once there is a session, claimStoredInvite() asks register-account
 *      (action claim_invite) to attribute it. The server decides everything:
 *      unknown or expired tokens, self-referral, an account older than the
 *      invite, and "already attributed" are all answered without error, and the
 *      unique key on referral_attributions makes the write happen once.
 *
 * It never blocks sign-in. A failure leaves the token in place for the next
 * page load; a terminal answer clears it.
 */
import { useEffect } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

const KEY = "mf_invite_ref";
const DONE_KEY = "mf_invite_claimed_for";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Answers after which asking again can never change anything.
const TERMINAL = new Set([
  "attributed", "already_attributed", "self_referral", "unknown_token",
  "expired", "account_predates_invite", "already_used", "not_an_invite",
]);

/** The invite token this browser is holding, or null. */
export function readStoredInviteRef(): string | null {
  try {
    const ft = (window as unknown as { mfFirstTouch?: () => Record<string, string> }).mfFirstTouch?.();
    if (ft?.invite_ref && UUID_RE.test(ft.invite_ref)) return ft.invite_ref.toLowerCase();
  } catch { /* fall through */ }
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const p = JSON.parse(raw) as { token?: string; at?: string };
      const at = Date.parse(p?.at || "");
      if (UUID_RE.test(p?.token || "") && at > 0 && Date.now() - at <= TTL_MS) return String(p.token).toLowerCase();
    }
  } catch { /* fall through */ }
  // /auth?ref=<token> opened directly, or the hand-off /geo-check makes through mf_ref.
  try {
    const q = new URLSearchParams(window.location.search).get("ref") || "";
    if (UUID_RE.test(q)) return q.toLowerCase();
  } catch { /* fall through */ }
  try {
    const m = localStorage.getItem("mf_ref") || "";
    if (UUID_RE.test(m)) return m.toLowerCase();
  } catch { /* fall through */ }
  return null;
}

function clearStoredInviteRef(userId: string) {
  try {
    localStorage.removeItem(KEY);
    if (UUID_RE.test(localStorage.getItem("mf_ref") || "")) localStorage.removeItem("mf_ref");
    localStorage.setItem(DONE_KEY, userId);
  } catch { /* storage off */ }
}

let inFlight: Promise<void> | null = null;

/** Attribute this account to its inviter if a token is held. Safe to call often. */
export function claimStoredInvite(given?: Session | null): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    const session = given ?? (await supabase.auth.getSession()).data.session;
    if (!session?.user) return;
    const token = readStoredInviteRef();
    const metaRef = String((session.user.user_metadata as Record<string, unknown> | undefined)?.mf_invite_ref || "");
    if (!token && !UUID_RE.test(metaRef)) return;
    try { if (!token && localStorage.getItem(DONE_KEY) === session.user.id) return; } catch { /* ask anyway */ }

    const { data, error } = await supabase.functions.invoke("register-account", {
      body: { action: "claim_invite", invite_token: token },
    });
    if (error) return; // transient: keep the token for the next page load
    const outcome = (data as { outcome?: string } | null)?.outcome || "";
    if (TERMINAL.has(outcome)) clearStoredInviteRef(session.user.id);
  })().catch(() => { /* attribution is never worth a broken page */ }).finally(() => { inFlight = null; });
  return inFlight;
}

/** Mounted once in App. Claims on load and on every sign-in, whatever the sign-in route. */
export function InviteClaimer(): null {
  useEffect(() => {
    void claimStoredInvite();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Deferred: awaiting auth calls inside this callback can deadlock the client.
      if (event === "SIGNED_IN" && session) setTimeout(() => { void claimStoredInvite(session); }, 0);
    });
    return () => subscription.unsubscribe();
  }, []);
  return null;
}
